const isLocalhost = () => window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '10.0.0.1';
const isHttps = () => window.location.protocol === 'https:';

type CookieOpts = {
    name: string;
    maxAge?: number; // seconds // omit maxAge for session cookie
    path?: string;
    domain?: string;
};

const DEFAULTS: CookieOpts = {
    name: 'kappla',
    domain: isLocalhost() ? undefined : window.location.hostname.split('.').slice(1).join('.'),
    maxAge: 60 * 60 * 24 * 365,
    path: '/',
};

/**
 * Cookie Security: Secure and SameSite flags
 *
 * - `Secure`: Ensures the cookie is only sent over HTTPS connections, preventing
 *   transmission over unencrypted HTTP where it could be intercepted.
 *   Skipped on localhost for local development convenience.
 *
 * - `SameSite=Lax`: Prevents the cookie from being sent in cross-site requests
 *   (e.g. when a third-party site submits a form to our domain), mitigating
 *   CSRF attacks. `Lax` allows the cookie on top-level navigations (e.g. clicking
 *   a link to our site) but blocks it on cross-origin subrequests.
 *
 * Note: While these cookies store user preferences (not auth tokens), applying
 * these flags is a defense-in-depth measure. The MSAL auth library manages its
 * own token cache in sessionStorage separately.
 */
const persistCookie = (options: CookieOpts, data: Record<string, string>) => {
    const { name = 'opp', maxAge, path, domain } = options;

    let cookie = `${name}=${encodeURIComponent(JSON.stringify(data))}`;

    if (maxAge) {
        cookie += `; max-age=${maxAge}`;
    }

    if (path) {
        cookie += `; path=${path}`;
    }

    if (domain) {
        cookie += `; domain=${domain}`;
    }

    cookie += '; SameSite=Lax';

    if (!isLocalhost() && isHttps()) {
        cookie += '; Secure';
    }

    document.cookie = cookie;
};

const readCookie = (cookieName: string): Record<string, string> => {
    const cookieEntry = document.cookie
        .split('; ')
        .find(cookie => cookie.startsWith(`${cookieName}=`));

    if (!cookieEntry) {
        return {};
    }

    const rawValue = cookieEntry.substring(cookieName.length + 1);

    const parsedValue = JSON.parse(decodeURIComponent(rawValue));

    return typeof parsedValue === 'object' && parsedValue !== null ? parsedValue : {};
};

const CookieJar = (options: CookieOpts) => {
    const combinedOptions = {
        ...DEFAULTS,
        ...options,
    };
    let data: Record<string, string> = readCookie(combinedOptions.name);

    return {
        set: (key: string, value: string) => {
            data[key] = value;
            persistCookie(combinedOptions, data);
        },

        get: (key: string, defaultValue?: string) => data[key] ?? defaultValue,

        removeCookie: (key: string) => {
            if (key in data) {
                delete data[key];

                if (Object.keys(data).length === 0) {
                    const removeCookie = (!isLocalhost() && isHttps())
                        ? `${combinedOptions.name}=; max-age=-1; path=${combinedOptions.path}; SameSite=Lax; Secure`
                        : `${combinedOptions.name}=; max-age=-1; path=${combinedOptions.path}; SameSite=Lax`;

                    document.cookie = removeCookie;
                } else {
                    persistCookie(combinedOptions, data);
                }
            }
        },

        removeAll: () => {
            document.cookie = `${combinedOptions.name}=; max-age=-1`;
            data = {};
            persistCookie(combinedOptions, data);
        },
    };
};

export default CookieJar;
